import React, { useCallback, useMemo, useRef, useState, VFC } from 'react';
import { Node } from 'src/common/interfaces/node.interface';
import { Journey } from 'src/common/interfaces/journey.interface';
import { Workspace } from 'src/common/interfaces/workspace.interface';
import { JourneyMenuPropsInterface } from 'src/common/JourneyMenu';
import { useSharePanelStore } from 'src/common/store/share.store';
import { useSlideOverStore } from 'src/store/slide-over';
import { Nullable } from 'src/types/nullable.type';
import { functionNoop } from 'src/utils/function/noop';
import { getAllNodes } from 'src/utils/journey';
import useDashboardStore from '../store/dashboard';
import { DashboardHomeDeleteConfirmPanel } from './delete-confirm-panel';
import { DashboardHomeDuplicateConfirmPanel } from './duplicate-confirm-panel';
import { DashboardHomeJourneysSettingsMenu } from './journeys/settings-menu';
import { DashboardHomeMovePanel } from './move-panel';
import { DashboardHomeNotificationPanel } from './notification-panel';
import { DashboardHomeStatusPanel } from './status-panel';
import { DashboardHomeSocialSharingPanel } from './social-sharing-panel';
import { DashboardHomeConversationsPanel } from './conversations/panel';
import { DashboardHomeConversationsPanelOrigin } from './conversations/origin';
import { JourneyAlias } from 'src/common/interfaces/journey/alias.interface';
import { DashboardHomeTagsPanel } from './tags-panel';
import { DashboardHomeConvertToOrganizationTemplatePanel } from 'src/dashboard/home/convert-to-organization-template-panel';
import { useChangeOwnershipStore } from 'src/common/components/change-ownership/store';
import { useCurrentOrganization } from 'src/store/organization';

interface DashboardHomeCardOptionsProps extends Pick<JourneyMenuPropsInterface, 'offset'> {
  journey: Journey;
  nestedMenuOffset?: JourneyMenuPropsInterface['offset'];
  nestedMenuPlacement: JourneyMenuPropsInterface['placement'];
  onMenuOpen: () => void;
  onMenuClose: () => void;
  onDeleteJourneyConfirm: (uuid: string) => void;
  onDuplicateJourneyConfirm: (uuid: string, detach_synced_step?: boolean) => void;
  onNotificationChange: (followed: boolean) => void;
  onSessionNotificationChange: (followed: boolean) => void;
  onStatusChange: (status: string) => void;
  onSocialSharingChange: (checked: boolean) => void;
  onWorkspaceChange: (workspace: Workspace) => void;
  isJourneyAccessible?: boolean;
  onRestrictedOperation?: (operationName: string) => void;
  renderMenuOrigin: (onClick: () => void) => any;
}

export type DashboardHomeActionPanelTypes =
  | 'insights'
  | 'status'
  | 'social_sharing'
  | 'share'
  | 'duplicate'
  | 'notifications'
  | 'delete'
  | 'create-template'
  | 'move'
  | 'conversations'
  | 'tags'
  | 'organization-templates'
  | 'update-ownership';

export const DashboardHomeCardOptions = ({
  journey,
  nestedMenuOffset,
  nestedMenuPlacement,
  offset,
  renderMenuOrigin,
  onDeleteJourneyConfirm,
  onDuplicateJourneyConfirm,
  onWorkspaceChange,
  onNotificationChange,
  onSessionNotificationChange,
  onStatusChange,
  onSocialSharingChange,
  onMenuOpen,
  onMenuClose,
  isJourneyAccessible = true,
  onRestrictedOperation,
}: DashboardHomeCardOptionsProps) => {
  const [conversationAliases, setConversationAliases] = useState([...journey.aliases]);
  const onJourneyCreateTemplateOpen = useDashboardStore((state) => state.onJourneyCreateTemplateOpen);
  const onJourneyShareOpen = useSharePanelStore((state) => state.onJourneyShareOpen);
  const appendJourneyOwnerShip = useChangeOwnershipStore((state) => state.appendJourneyOwnerShip);
  const openSlideOver = useSlideOverStore((state) => state.openSlideOver);

  const { currentOrganization } = useCurrentOrganization();

  const [activePanel, setActivePanel] = useState<Nullable<DashboardHomeActionPanelTypes>>(null);

  const settingsMenuOpenRef = useRef(functionNoop);
  const settingsMenuCloseRef = useRef(functionNoop);

  const conversationsPanelOpenRef = useRef(functionNoop);
  const conversationsPanelCloseRef = useRef(functionNoop);

  const duplicateMenuOpenRef = useRef(functionNoop);
  const duplicateMenuCloseRef = useRef(functionNoop);

  const deleteMenuOpenRef = useRef(functionNoop);
  const deleteMenuCloseRef = useRef(functionNoop);

  const notificationsMenuOpenRef = useRef(functionNoop);
  const notificationsMenuCloseRef = useRef(functionNoop);

  const statusMenuOpenRef = useRef(functionNoop);
  const statusMenuCloseRef = useRef(functionNoop);

  const moveMenuOpenRef = useRef(functionNoop);
  const moveMenuCloseRef = useRef(functionNoop);

  const tagsOpenRef = useRef(functionNoop);
  const tagsCloseRef = useRef(functionNoop);

  const organizationTemplatesMenuOpenRef = useRef(functionNoop);
  const organizationTemplatesMenuCloseRef = useRef(functionNoop);

  const onMenuClicked = () => {
    settingsMenuOpenRef.current();
  };

  const onBack = () => {
    setActivePanel(null);
    deleteMenuCloseRef.current();
    setTimeout(onMenuClicked, 50);
  };

  const onClosePanel = () => {
    setActivePanel(null);
    onMenuClose();
  };

  const onDeleteJourneyConfirmed = () => {
    onDeleteJourneyConfirm(journey.uuid);
    onClosePanel();
  };

  const onDuplicateJourneyConfirmed = useCallback(
    (detach_synced_step = false) => {
      onDuplicateJourneyConfirm(journey.uuid, detach_synced_step);
    },
    [journey.uuid, onDuplicateJourneyConfirm]
  );

  const isThereAnySyncedStep = useMemo(() => getAllNodes(journey).some((node: Node) => node.is_synced), []);

  const onMenuItemClicked = useCallback(
    (activePanel: DashboardHomeActionPanelTypes) => {
      // Always allow delete operations
      if (activePanel === 'delete') {
        setActivePanel(activePanel);
        return;
      }

      // Check if journey is accessible for other operations
      if (!isJourneyAccessible && onRestrictedOperation) {
        const operationNames: Record<DashboardHomeActionPanelTypes, string> = {
          insights: 'view insights for',
          status: 'change status of',
          social_sharing: 'configure social sharing for',
          share: 'share',
          duplicate: 'duplicate',
          notifications: 'configure notifications for',
          'create-template': 'create template from',
          move: 'move',
          conversations: 'configure conversations for',
          tags: 'manage tags for',
          'organization-templates': 'convert to organization template',
          'update-ownership': 'change ownership of',
          delete: 'delete',
        };
        
        onRestrictedOperation(operationNames[activePanel] || 'perform this operation on');
        return;
      }

      if (activePanel === 'duplicate' && !isThereAnySyncedStep) {
        onDuplicateJourneyConfirmed();
      } else if (activePanel === 'create-template') {
        onJourneyCreateTemplateOpen(journey);
        openSlideOver('template');
      } else if (activePanel === 'share') {
        onJourneyShareOpen(journey);
      } else if (activePanel === 'update-ownership') {
        if (!currentOrganization) {
          return;
        }
        const journeyWorkspaceId = journey.workspace.id;
        const workspaceMembers =
          currentOrganization.workspaces.find((workspace: Workspace) => workspace.id === journeyWorkspaceId)?.members ||
          [];
        appendJourneyOwnerShip(journey, workspaceMembers);
        openSlideOver('ownership');
      }

      setActivePanel(activePanel);
    },
    [
      isJourneyAccessible,
      onRestrictedOperation,
      isThereAnySyncedStep,
      onDuplicateJourneyConfirmed,
      onJourneyCreateTemplateOpen,
      journey,
      openSlideOver,
      onJourneyShareOpen,
    ]
  );

  const onConversationPanelOriginClicked = () => {
    onMenuItemClicked('conversations');
  };

  const onAliasConversationChanged = (uuid: JourneyAlias['uuid'], value: boolean) => {
    setConversationAliases((aliases) =>
      aliases.map((alias) => ({
        ...alias,
        ...(alias.uuid === uuid ? { is_subscribed_to_comments_status: value ? 'enabled' : 'disabled' } : {}),
      }))
    );
  };

  const onConversationPanelBackClicked = () => {
    setActivePanel('notifications');
  };

  if (activePanel === 'delete') {
    return (
      <DashboardHomeDeleteConfirmPanel
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
        offset={nestedMenuOffset || offset}
        placement={nestedMenuPlacement || 'bottom-start'}
        openRef={deleteMenuOpenRef}
        closeRef={deleteMenuCloseRef}
        content='Deleting this Journey is an irreversible action'
        confirmText='Yes, delete'
        denyText='No, keep'
        onOpen={onMenuOpen}
        onClose={onClosePanel}
        onConfirm={onDeleteJourneyConfirmed}
        onDeny={onClosePanel}
        onBack={onBack}
      />
    );
  } else if (activePanel === 'organization-templates') {
    return (
      <DashboardHomeConvertToOrganizationTemplatePanel
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
        offset={nestedMenuOffset || offset}
        placement={nestedMenuPlacement || 'bottom-start'}
        openRef={organizationTemplatesMenuOpenRef}
        closeRef={organizationTemplatesMenuCloseRef}
        onOpen={onMenuOpen}
        onClose={onClosePanel}
        onBack={onBack}
        journey={journey}
      />
    );
  } else if (activePanel === 'duplicate' && isThereAnySyncedStep) {
    return (
      <DashboardHomeDuplicateConfirmPanel
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
        openRef={duplicateMenuOpenRef}
        closeRef={duplicateMenuCloseRef}
        placement={nestedMenuPlacement || 'bottom-start'}
        onOpen={onMenuOpen}
        content='The Journey you are duplicating contains Synced Steps. Would you like to keep the Steps synced or  would you like to detach them so you can edit freely'
        confirmText='Keep synced'
        denyText='Detach'
        onClose={onClosePanel}
        onConfirm={() => onDuplicateJourneyConfirmed(false)}
        onDeny={() => onDuplicateJourneyConfirmed(true)}
        onBack={onBack}
      />
    );
  } else if (activePanel === 'move') {
    return (
      <DashboardHomeMovePanel
        openRef={moveMenuOpenRef}
        journeyWorkspace={journey.workspace}
        closeRef={moveMenuCloseRef}
        placement={nestedMenuPlacement || 'bottom-start'}
        onOpen={onMenuOpen}
        onBack={onBack}
        onClose={onClosePanel}
        onWorkspaceChanged={onWorkspaceChange}
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
      />
    );
  } else if (activePanel === 'tags') {
    return (
      <DashboardHomeTagsPanel
        journey={journey}
        openRef={tagsOpenRef}
        journeyTags={journey.tags.map((tag) => tag.id)}
        closeRef={tagsCloseRef}
        placement={nestedMenuPlacement || 'bottom-start'}
        onOpen={onMenuOpen}
        onBack={onBack}
        onClose={onClosePanel}
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
      />
    );
  } else if (activePanel === 'status') {
    return (
      <DashboardHomeStatusPanel
        openRef={statusMenuOpenRef}
        closeRef={statusMenuCloseRef}
        onOpen={onMenuOpen}
        onClose={onClosePanel}
        placement={nestedMenuPlacement || 'bottom-start'}
        onChange={onStatusChange}
        onBack={onBack}
        isEnabled={journey.status === 'public'}
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
      ></DashboardHomeStatusPanel>
    );
  } else if (activePanel === 'social_sharing') {
    return (
      <DashboardHomeSocialSharingPanel
        openRef={statusMenuOpenRef}
        closeRef={statusMenuCloseRef}
        onOpen={onMenuOpen}
        onClose={onClosePanel}
        placement={nestedMenuPlacement || 'bottom-start'}
        onChange={onSocialSharingChange}
        onBack={onBack}
        isEnabled={journey.social_sharing}
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
      ></DashboardHomeSocialSharingPanel>
    );
  } else if (activePanel === 'notifications') {
    return (
      <DashboardHomeNotificationPanel
        openRef={notificationsMenuOpenRef}
        closeRef={notificationsMenuCloseRef}
        onOpen={onMenuOpen}
        onClose={onClosePanel}
        placement={nestedMenuPlacement || 'bottom-start'}
        onChange={onNotificationChange}
        onSessionChange={onSessionNotificationChange}
        onBack={onBack}
        isEnabled={journey.is_followed || false}
        isSessionEnabled={journey.is_session_followed || false}
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
      >
        <DashboardHomeConversationsPanelOrigin onClick={onConversationPanelOriginClicked} />
      </DashboardHomeNotificationPanel>
    );
  } else if (activePanel === 'conversations') {
    return (
      <DashboardHomeConversationsPanel
        aliases={conversationAliases}
        onChange={onAliasConversationChanged}
        placement={nestedMenuPlacement || 'bottom-start'}
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
        onBack={onConversationPanelBackClicked}
        onOpen={onMenuOpen}
        onClose={onClosePanel}
        openRef={conversationsPanelOpenRef}
        closeRef={conversationsPanelCloseRef}
      />
    );
  }

  return (
    <DashboardHomeJourneysSettingsMenu
      journey={journey}
      onOpen={onMenuOpen}
      openRef={settingsMenuOpenRef}
      closeRef={settingsMenuCloseRef}
      onClose={onMenuClose}
      onItemClicked={onMenuItemClicked}
      isJourneyAccessible={isJourneyAccessible}
      onRestrictedOperation={onRestrictedOperation}
      renderMenuOrigin={renderMenuOrigin}
    />
  );
};
